<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { fade } from 'svelte/transition';

  import type { Playlist, PlaylistItem } from './api';
  import { recordPlayback } from './playback-tracker';
  import { precacheMedia } from './media-cache';

  export let playlist: Playlist;

  let currentIndex = 0;
  let currentItem: PlaylistItem | null = null;
  let itemStartedAt = Date.now();
  let advanceTimer: ReturnType<typeof setTimeout> | null = null;

  $: items = playlist.items;
  $: if (items.length > 0) {
    // Reset al recibir nueva playlist
    currentIndex = currentIndex % items.length;
    showItem(currentIndex);
  }

  function showItem(index: number) {
    if (items.length === 0) {
      currentItem = null;
      return;
    }

    // Si había un item anterior, registrar su playback
    if (currentItem) {
      const playedSec = Math.floor((Date.now() - itemStartedAt) / 1000);
      recordPlayback({
        mediaAssetId: currentItem.mediaAssetId,
        playlistId: playlist.id,
        playedAt: new Date(itemStartedAt).toISOString(),
        durationPlayedSeconds: playedSec,
        completed: playedSec >= currentItem.durationSeconds,
      });
    }

    currentIndex = (index + items.length) % items.length;
    currentItem = items[currentIndex] ?? null;
    itemStartedAt = Date.now();

    if (!currentItem) return;

    // Programar el siguiente. Para video, esperamos al onended; aún así fijamos
    // timer como safety net por si el video falla.
    if (advanceTimer) clearTimeout(advanceTimer);
    const durationMs = currentItem.durationSeconds * 1000;
    advanceTimer = setTimeout(() => showItem(currentIndex + 1), durationMs);

    // Pre-cache de los próximos 3 items
    const nextUrls: string[] = [];
    for (let i = 1; i <= 3; i++) {
      const next = items[(currentIndex + i) % items.length];
      if (next) nextUrls.push(next.mediaAsset.cdnUrl);
    }
    void precacheMedia(nextUrls);
  }

  onMount(() => {
    showItem(0);
  });

  onDestroy(() => {
    if (advanceTimer) clearTimeout(advanceTimer);
    // Registrar último playback al destruir
    if (currentItem) {
      const playedSec = Math.floor((Date.now() - itemStartedAt) / 1000);
      recordPlayback({
        mediaAssetId: currentItem.mediaAssetId,
        playlistId: playlist.id,
        playedAt: new Date(itemStartedAt).toISOString(),
        durationPlayedSeconds: playedSec,
        completed: false,
      });
    }
  });

  function handleVideoEnded() {
    // Video terminó antes del timer → avanzamos ya
    showItem(currentIndex + 1);
  }
</script>

<div class="carousel">
  {#if currentItem}
    {#key currentItem.id}
      <div class="slide" transition:fade={{ duration: 600 }}>
        {#if currentItem.mediaAsset.type === 'VIDEO'}
          <video
            src={currentItem.mediaAsset.cdnUrl}
            autoplay
            muted
            playsinline
            on:ended={handleVideoEnded}
          />
        {:else}
          <img src={currentItem.mediaAsset.cdnUrl} alt="" />
        {/if}
      </div>
    {/key}
  {/if}
</div>

<style>
  .carousel {
    position: absolute;
    inset: 0;
    background: #000;
  }
  .slide {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .slide img,
  .slide video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
</style>
